import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ClientList from './components/ClientList';
import ClientForm from './components/ClientForm';
import History from './components/History';
import Users from './components/Users';
import Reports from './components/Reports';
import Tickets from './components/Tickets';
import SupportHours from './components/SupportHours';
import ClientActivityHistory from './components/ClientActivityHistory';
import DataUsage from './components/DataUsage';
import AllDevices from './components/AllDevices';
import DataManagement from './components/DataManagement';
import PlatformManager from './components/PlatformManager';
import Alerts from './components/Alerts';
import ClientFleetDashboard from './components/ClientFleetDashboard';
import InstallCheck from './components/InstallCheck';
import { generateBatteryReport } from './utils/batteryReport';
import ClientRegistration from './components/ClientRegistration';
import ClientPasswordChange from './components/ClientPasswordChange';
import PasswordReset from './components/PasswordReset';
import { Client } from './types';
import './index.css';

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [currentView, setCurrentView] = useState<'dashboard' | 'list' | 'form' | 'history' | 'users' | 'reports' | 'tickets' | 'support-hours' | 'alerts' | 'activity' | 'data-usage' | 'device-management' | 'data-management' | 'camel' | 'all-devices' | 'fleet-dashboard' | 'install-check'>('dashboard');
  const [ticketPrefill, setTicketPrefill] = useState<{ unitId: string; name: string; platform: string; issue: string } | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [platformHexValue, setPlatformHexValue] = useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = useState<'camel' | 'starlink'>('camel');
  const [reportLoading, setReportLoading] = useState(false);

  // Check for both admin and client authentication
  const clientToken = localStorage.getItem('clientToken');
  const clientUser = localStorage.getItem('clientUser');
  const isClientAuthenticated = !!(clientToken && clientUser);
  
  // Get current user (admin or client)
  let currentUser: any = user;
  if (!currentUser && clientUser && clientUser !== 'undefined') {
    try {
      currentUser = JSON.parse(clientUser);
    } catch (e) {
      console.error('Failed to parse clientUser:', e);
      currentUser = null;
    }
  }
  const isClient = currentUser?.type === 'client' || currentUser?.type === 'client_user';

  // Define permissions based on user type - memoized to prevent useEffect dependency issues
  const permissions = useMemo(() => {
    if (isClient) {
      return ['tickets', 'fleet-dashboard', 'install-check'];
    }
    // Check user role for admin users
    const userRole = currentUser?.role || 'view_only';
    if (userRole === 'view_only') {
      return ['reports', 'camel']; // View-only users can only see reports and platforms
    }
    // Admin users get full access
    return ['list', 'users', 'history', 'reports', 'tickets', 'support-hours', 'activity', 'data-usage', 'device-management', 'data-management', 'camel', 'alerts', 'install-check'];
  }, [isClient, currentUser?.role]);
  
  // Redirect clients to their default allowed page
  React.useEffect(() => {
    if (isClient && !permissions.includes(currentView)) {
      setCurrentView('fleet-dashboard');
    }
  }, [isClient, currentView, permissions]);

  // Handle platform navigation from InstallCheck
  React.useEffect(() => {
    const platformView = localStorage.getItem('platformView');
    const platformSearch = localStorage.getItem('platformSearch');
    if (platformView && platformSearch) {
      localStorage.removeItem('platformView');
      localStorage.removeItem('platformSearch');
      setCurrentView(platformView as any);
      setSelectedPlatform(platformView as 'camel' | 'starlink');
      setPlatformHexValue(platformSearch);
    }
  }, []);

  const handleLogout = () => {
    // Clear both admin and client tokens
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('username');
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientUser');
    
    // Redirect to main page
    window.location.href = '/';
  };

  const [showWelcome, setShowWelcome] = useState(false);
  const displayName = currentUser?.name || currentUser?.username || 'there';

  useEffect(() => {
    if ((isAuthenticated || isClientAuthenticated) && !sessionStorage.getItem('welcomed')) {
      sessionStorage.setItem('welcomed', '1');
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 4000);
    }
  }, [isAuthenticated, isClientAuthenticated]);

  if (!isAuthenticated && !isClientAuthenticated) {
    return <Login />;
  }

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setCurrentView('form');
  };

  const handleAddClient = () => {
    setSelectedClient(undefined);
    setCurrentView('form');
  };

  const handleSave = () => {
    setCurrentView('list');
    setSelectedClient(undefined);
  };

  const handleCancel = () => {
    setCurrentView('list');
    setSelectedClient(undefined);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#EFEFF4' }}>
      {showWelcome && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowWelcome(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', padding: '48px 40px', maxWidth: '420px', width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', animation: 'fadeIn 0.3s ease' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👋</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '24px', color: '#1a1a2e' }}>Welcome back, {displayName}!</h2>
            <p style={{ margin: '0 0 24px', color: '#666', fontSize: '15px', lineHeight: '1.5' }}>
              {isClient ? "We're glad you're here. If you need anything, just raise a ticket and our team will be right with you." : "Great to see you. Let's make today a productive one."}
            </p>
            <button onClick={() => setShowWelcome(false)} style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px 32px', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}>Let's Go! 🚀</button>
          </div>
        </div>
      )}
      <header style={{ 
        background: 'rgba(240,245,255,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        color: '#1d1d1f',
        padding: '0',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div className="container" style={{ padding: '0 20px', maxWidth: '100%' }}>
          <div className="flex justify-between align-center" style={{ height: '60px' }}>
            <div className="flex align-center" style={{ gap: '16px', flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <img 
                  src="/assets/logos/optitrac-logo.svg" 
                  alt="Optitrac Logo" 
                  style={{ 
                    height: '40px', 
                    width: 'auto'
                  }} 
                />
              </div>
              <nav style={{ 
                display: 'flex', 
                gap: '6px', 
                alignItems: 'center', 
                flex: 1, 
                overflowX: 'auto', 
                overflowY: 'hidden',
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: '4px',
                marginBottom: '-4px'
              }}>
                {!isClient && (
                  <button onClick={() => setCurrentView('dashboard')} className={`nav-button ${currentView === 'dashboard' ? 'active' : ''}`}>
                    🏠 Home
                  </button>
                )}
                {permissions.includes('list') && (
                  <button onClick={() => setCurrentView('list')} className={`nav-button ${currentView === 'list' ? 'active' : ''}`}>
                    📋 Clients
                  </button>
                )}
                {!isClient && (
                  <button onClick={() => setCurrentView('all-devices')} className={`nav-button ${currentView === 'all-devices' ? 'active' : ''}`}>
                    📱 All Devices
                  </button>
                )}
                {!isClient && (
                  <button onClick={() => setCurrentView('camel')} className={`nav-button ${currentView === 'camel' ? 'active' : ''}`}>
                    🚀 Platforms
                  </button>
                )}
                {permissions.includes('users') && (
                  <button onClick={() => setCurrentView('users')} className={`nav-button ${currentView === 'users' ? 'active' : ''}`}>
                    👥 Users
                  </button>
                )}
                {permissions.includes('history') && (
                  <button onClick={() => setCurrentView('history')} className={`nav-button ${currentView === 'history' ? 'active' : ''}`}>
                    📜 History
                  </button>
                )}
                {permissions.includes('reports') && (
                  <button onClick={() => setCurrentView('reports')} className={`nav-button ${currentView === 'reports' ? 'active' : ''}`}>
                    📊 Reports
                  </button>
                )}
                {permissions.includes('fleet-dashboard') && (
                  <button onClick={() => setCurrentView('fleet-dashboard')} className={`nav-button ${currentView === 'fleet-dashboard' ? 'active' : ''}`}>
                    🚛 Fleet
                  </button>
                )}
                {permissions.includes('tickets') && (
                  <button onClick={() => setCurrentView('tickets')} className={`nav-button ${currentView === 'tickets' ? 'active' : ''}`}>
                    🎫 Tickets
                  </button>
                )}
                {permissions.includes('support-hours') && (
                  <button onClick={() => setCurrentView('support-hours')} className={`nav-button ${currentView === 'support-hours' ? 'active' : ''}`}>
                    ⏱️ Support Hours
                  </button>
                )}
                {permissions.includes('alerts') && (
                  <button onClick={() => setCurrentView('alerts')} className={`nav-button ${currentView === 'alerts' ? 'active' : ''}`}>
                    🚨 Alerts
                  </button>
                )}
                {permissions.includes('install-check') && (
                  <button onClick={() => setCurrentView('install-check')} className={`nav-button ${currentView === 'install-check' ? 'active' : ''}`}>
                    🔧 Install Check
                  </button>
                )}
                {permissions.includes('activity') && (
                  <button onClick={() => setCurrentView('activity')} className={`nav-button ${currentView === 'activity' ? 'active' : ''}`}>
                    📈 Activity
                  </button>
                )}
                {permissions.includes('data-usage') && (
                  <button onClick={() => setCurrentView('data-usage')} className={`nav-button ${currentView === 'data-usage' ? 'active' : ''}`}>
                    📡 Data Usage
                  </button>
                )}
              </nav>
            </div>
            <div className="flex align-center" style={{ gap: '12px', flexShrink: 0 }}>
              <div style={{ 
                fontSize: '13px',
                color: '#6b7280',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}>
                <span style={{ color: '#9ca3af' }}>Welcome,</span> <strong style={{ color: '#1f2937' }}>{currentUser?.username}</strong>
              </div>
              <button
                onClick={handleLogout}
                className="btn btn-danger"
                style={{ 
                  fontSize: '14px',
                  padding: '8px 16px'
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        {currentView === 'dashboard' ? (
          <Dashboard 
            onNavigate={(view) => setCurrentView(view as any)}
            userName={currentUser?.username || 'User'}
          />
        ) : currentView === 'list' ? (
          <ClientList 
            onSelectClient={handleSelectClient}
            onAddClient={handleAddClient}
          />
        ) : currentView === 'users' ? (
          <Users />
        ) : currentView === 'history' ? (
          <History />
        ) : currentView === 'reports' ? (
          <Reports />
        ) : currentView === 'fleet-dashboard' ? (
          <ClientFleetDashboard onCreateTicket={(vehicle) => {
            setTicketPrefill(vehicle);
            setCurrentView('tickets');
          }} />
        ) : currentView === 'tickets' ? (
          <Tickets prefillTicket={ticketPrefill} />
        ) : currentView === 'support-hours' ? (
          <SupportHours />
        ) : currentView === 'alerts' ? (
          <Alerts />
        ) : currentView === 'install-check' ? (
          <InstallCheck />
        ) : currentView === 'activity' ? (
          <ClientActivityHistory />
        ) : currentView === 'data-usage' ? (
          <DataUsage />
        ) : currentView === 'all-devices' ? (
          <AllDevices 
            onNavigateToPlatform={(platform, hexValue) => {
              setSelectedPlatform(platform);
              setPlatformHexValue(hexValue);
              setCurrentView('camel');
            }}
          />
        ) : currentView === 'device-management' ? (
          <AllDevices />
        ) : currentView === 'data-management' ? (
          <DataManagement />
        ) : currentView === 'camel' ? (
          <PlatformManager 
            initialUnitId={platformHexValue} 
            selectedPlatform={selectedPlatform}
            onClearHexValue={() => setPlatformHexValue('')} 
          />
        ) : (
          <ClientForm
            client={selectedClient}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes (no auth required) */}
          <Route path="/portal/register" element={<ClientRegistration />} />
          <Route path="/portal/change-password" element={<ClientPasswordChange />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          
          {/* All other routes require authentication */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
